import React from "react";
import {
  Avatar,
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ShareOutlined as ShareIcon,
  Verified as VerifiedIcon,
  PlayArrow as PlayIcon,
} from "@mui/icons-material";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { CommonButton } from "../../../../Components/ui";
import { getSymbolsByCategory } from "./fiveSlotConfig";
import { DEFAULT_CURRENCY } from "../../../../../static/Constants";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";
import FiveSlotSymbolIcon from "./FiveSlotSymbolIcon";

const FiveSlotMachineCard = ({ lotteryData }) => {
  const history = useHistory();

  const category =
    lotteryData?.slotCategory ||
    (lotteryData?.name?.toLowerCase().includes("crypto") ? "crypto" : "fiat");
  const currency = lotteryData?.currency || DEFAULT_CURRENCY || "POL";
  const activeSymbols = getSymbolsByCategory(category);

  // Preview all 5 symbols in order: [0, 1, 2, 3, 4]
  const previewSymbols = [
    activeSymbols[0],
    activeSymbols[1],
    activeSymbols[2],
    activeSymbols[3],
    activeSymbols[4],
  ];

  const handleOpenLottery = () => {
    history.push(`/five-slotlottery/${lotteryData.lotteryId}`);
  };

  const handleShare = (event) => {
    event.stopPropagation();
    const url = `${window.location.origin}/five-slotlottery/${lotteryData.lotteryId}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url);
      toast.success("Slot machine link copied!");
    }
  };

  const formatAmount = (amount) => {
    const value = Number(amount);
    if (Number.isNaN(value)) {
      return "0.00";
    }
    return value.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <Box
      className="slot-machine-card five-slot-card"
      sx={{
        cursor: "pointer",
        transition: "transform 0.25s ease, box-shadow 0.25s ease",
        borderRadius: borderRadiusPx.xl,
        background: "linear-gradient(180deg, #0b1220 0%, #060b14 100%)",
        border: "1.5px solid rgba(234, 179, 8, 0.4)",
        boxShadow: "0 6px 20px rgba(0, 0, 0, 0.5)",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 12px 30px rgba(234, 179, 8, 0.25)",
          borderColor: "#eab308",
        },
        p: { xs: 2, sm: 2.5 },
      }}
      onClick={handleOpenLottery}
    >
      {/* Card Header */}
      <Box sx={{ mb: 1.5 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                background: "radial-gradient(circle, #f97316 0%, #dc2626 100%)",
                color: "#ffffff",
                fontWeight: 900,
                fontSize: "1.1rem",
                boxShadow: "0 2px 8px rgba(220, 38, 38, 0.5)",
              }}
            >
              5
            </Avatar>

            <Box>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 800,
                    color: "#ffffff",
                  }}
                >
                  {lotteryData.name || `5-SlotBET #${lotteryData.lotteryId}`}
                </Typography>
                <VerifiedIcon
                  sx={{
                    fontSize: 14,
                    color: "#f59e0b",
                  }}
                />
              </Stack>
              <Typography
                variant="caption"
                sx={{
                  color: "#f59e0b",
                  fontWeight: 800,
                  fontSize: "0.72rem",
                  textTransform: "uppercase",
                }}
              >
                {category === "crypto" ? "5-Reel Crypto Pokies" : "5-Reel Fiat Pokies"}
              </Typography>
            </Box>
          </Stack>

          <Tooltip title="Share Slot Machine" arrow>
            <IconButton
              size="small"
              onClick={handleShare}
              sx={{
                color: "#94a3b8",
                p: 0.5,
                "&:hover": { color: "#ffffff" },
              }}
            >
              <ShareIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Jackpots Strip (Mega, Major, Mini) */}
      <Box
        sx={{
          py: 1,
          px: 1,
          borderRadius: "10px",
          bgcolor: "rgba(15, 23, 42, 0.8)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          mb: 2,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box sx={{ flex: 1, textAlign: "center" }}>
            <Typography
              variant="caption"
              sx={{
                color: "#fca5a5",
                fontWeight: 800,
                fontSize: "0.6rem",
              }}
            >
              MEGA
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 900,
                color: "#fef08a",
                fontSize: "0.82rem",
              }}
            >
              {currency} {formatAmount(lotteryData?.jackpots?.mega?.amount || 128900)}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, textAlign: "center", borderLeft: "1px solid rgba(255,255,255,0.1)" }}>
            <Typography
              variant="caption"
              sx={{
                color: "#e9d5ff",
                fontWeight: 800,
                fontSize: "0.6rem",
              }}
            >
              MAJOR
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 900,
                color: "#fef08a",
                fontSize: "0.82rem",
              }}
            >
              {currency} {formatAmount(lotteryData?.jackpots?.major?.amount || 16450)}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, textAlign: "center", borderLeft: "1px solid rgba(255,255,255,0.1)" }}>
            <Typography
              variant="caption"
              sx={{
                color: "#bbf7d0",
                fontWeight: 800,
                fontSize: "0.6rem",
              }}
            >
              MINI
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 900,
                color: "#fef08a",
                fontSize: "0.82rem",
              }}
            >
              {currency} {formatAmount(lotteryData?.jackpots?.mini?.amount || 2840.5)}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* 5-Reels Mini Preview */}
      <Box
        sx={{
          borderRadius: "14px",
          background: "linear-gradient(180deg, #fef08a 0%, #eab308 50%, #854d0e 100%)",
          p: "3px",
          mb: 2,
          boxShadow: "inset 0 1px 3px rgba(255,255,255,0.8), 0 4px 12px rgba(0,0,0,0.5)",
        }}
      >
        <Box
          sx={{
            bgcolor: "#050b14",
            borderRadius: "11px",
            py: 1.25,
            px: 0.5,
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: 0.5,
            alignItems: "center",
          }}
        >
          {previewSymbols.map((sym, idx) => (
            <Box
              key={idx}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#090f1d",
                p: 0.5,
                borderRadius: "8px",
                border: `1px solid ${sym.tierColor || "rgba(234, 179, 8, 0.3)"}`,
              }}
            >
              <FiveSlotSymbolIcon symbol={sym} size="sm" />
              <Typography
                sx={{
                  fontSize: "0.58rem",
                  fontWeight: 800,
                  color: "#ffffff",
                  mt: 0.35,
                }}
              >
                {sym.multiplier}x
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Play Action Button */}
      <CommonButton
        variant="primary"
        size="small"
        fullWidth
        startIcon={<PlayIcon />}
        onClick={handleOpenLottery}
        sx={{
          py: 1,
          borderRadius: "10px",
          fontWeight: 900,
          background: "linear-gradient(180deg, #fef08a 0%, #eab308 50%, #b45309 100%)",
          color: "#451a03",
          border: "1px solid #ffffff",
          letterSpacing: "1px",
          fontSize: "0.9rem",
          boxShadow: "0 4px 12px rgba(234, 179, 8, 0.4)",
          "&:hover": {
            background: "linear-gradient(180deg, #ffffff 0%, #fde047 50%, #ca8a04 100%)",
          },
        }}
      >
        PLAY 5-SLOT
      </CommonButton>
    </Box>
  );
};

export default FiveSlotMachineCard;